/**
 * A210C Web Serial 客户端
 *
 * 帧路由：CDC 字节流按「2 字节头 + 已知定长」切帧（与 wilwei 调试页 / ADM sync-core 口径一致）：
 *  - 0x0A 0x00  64B  端口信息（请求 [0x02,0x00]，主轮询）
 *  - 0x0A 0x02  42B  专注记录（请求 [0x02,0x0A]，可带 1 字节开关）
 *  - 0x0A 0x16   7B  屏幕设置（请求 [0x02,0x16]）：brightness/lockTime/language/screensaverOn
 *  - 0x0A 0x18   5B  当前屏保（请求 [0x02,0x18]）：slot + crc16(BE)
 *  - 0x0A 0x20   3B  屏保上传 Info 应答（0x00=OK 0x01=busy 0x02=backup_fail 0x03=crc_err）
 *  - 0x0A 0x21  13B  屏保上传完成应答（status[2]：0x10=完成 0x11=失败）
 *  - 0x0A 0x27   9B  显示器物理尺寸（请求 [0x02,0x27]）
 *  - 0x0A 0x30  18B  固件版本（请求 [0x02,0x30]，固件 2.1.5 起）：MCU(8B ASCII) + UI/flash(8B ASCII)
 *  - 0x0A 0x36   6B  PD ADC 实测功率（请求 [0x02,0x36]）
 *  - 0x0B 0x0A   3B  恢复出厂应答（status 0x00=已接受；旧固件 0xCE）
 * 其它字节逐个丢弃（同步到已知头）。
 *
 * 端口信息返回后立即跟发电源快照；收到功率帧即刷新，旧固件回退 0x0236。
 *
 * 设置写指令（无应答，fire-and-forget）：
 *  - 亮度 [0x02,0x12,v(10-100)]、锁屏 [0x02,0x11,v(0-5)]、语言 [0x02,0x10,v(0-3)]、屏保开关 [0x02,0x1F,on]
 */
import { Praser } from '@/protocol'
import type { PortInfoData, FocusRecordingData } from '@/device-ui'
import {
  buildScreensaverInfoPacket,
  buildScreensaverDataPacket,
  buildSetScreensaverByCrc,
  buildReadHandshake365,
  buildReadInfo265,
  buildReadData265,
  parseReadInfo065,
  parseReadData065,
  SS_PKT_DATA_LEN,
  SS_READ_PAYLOAD_MAX,
} from '@/services/a210c-screensaver'

// A210C CDC USB 枚举标识（与 wilwei 调试工具一致）
export const A210C_USB_VID = 0x291a
export const A210C_USB_PID = 0x010c

const POLL_INTERVAL_MS = 1000
const FIRST_POLL_DELAY_MS = 400
const BAUD_RATE = 115200
/** 暂停轮询后等在途响应（0x0A00 等）落地的静默期，再开始独占传输 */
const TRANSFER_SETTLE_MS = 300
const SCREEN_SIZE_CACHE_TTL_MS = 8000
const PD_ADC_CACHE_TTL_MS = 6000
const POWER_SNAPSHOT_WAIT_MS = 900
const POWER_SNAPSHOT_FALLBACK_AFTER = 3
const POLL_PATTERN = [
  'host-conn',
  'port-info',
  'screen-size',
  'host-conn',
  'port-info',
] as const
type PollKind = (typeof POLL_PATTERN)[number]

/**
 * 上位机连接心跳（对齐 wilwei 调试页）：请求 [0x02,0x38,status]。
 * status 0x02=已连接(WebUSB)，0x00=断开。设备端 10s 未收到即回到
 * 「Web / App Control Not Connected」界面，故随轮询每 ~3s 发一次。
 */
const HOST_CONN_STATUS_CONNECTED = 0x02
const HOST_CONN_STATUS_DISCONNECT = 0x00

/** 番茄/健康提醒族的秒数参数统一为 uint32 小端 */
function encodeU32LE(value: number): number[] {
  const v = value >>> 0
  return [v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff]
}

/** 已知响应帧：2 字节头 → 帧总长 */
const FRAME_TABLE: Array<{ h0: number; h1: number; len: number }> = [
  { h0: 0x0a, h1: 0x00, len: 64 }, // 端口信息
  { h0: 0x0a, h1: 0x02, len: 42 }, // 专注记录
  { h0: 0x0a, h1: 0x16, len: 7 }, // 屏幕设置
  { h0: 0x0a, h1: 0x18, len: 5 }, // 当前屏保
  { h0: 0x0a, h1: 0x20, len: 3 }, // 屏保 Info 应答
  { h0: 0x0a, h1: 0x21, len: 13 }, // 屏保完成应答
  { h0: 0x0a, h1: 0x27, len: 9 }, // 显示器物理尺寸
  { h0: 0x0a, h1: 0x30, len: 18 }, // 固件版本（MCU 8B + UI/flash 8B）
  { h0: 0x0a, h1: 0x38, len: 3 }, // 上位机心跳应答（部分固件会回，吃掉防错位）
  { h0: 0x0a, h1: 0x1a, len: 3 }, // 屏保删除应答
  { h0: 0x0a, h1: 0x1b, len: 3 }, // 屏保轮播开关应答
  { h0: 0x0a, h1: 0x1c, len: 8 }, // 屏保轮播状态
  { h0: 0x0a, h1: 0x1d, len: 4 }, // 屏保轮播间隔应答
  { h0: 0x0a, h1: 0x36, len: 6 }, // PD ADC 实测功率
  { h0: 0x0a, h1: 0x39, len: 16 }, // 同帧电源快照（与 ADM 一致）
  { h0: 0x0b, h1: 0x0a, len: 3 }, // 恢复出厂应答
  { h0: 0x0a, h1: 0x03, len: 22 }, // 专注/健康提醒参数（请求 0x020B，响应头不是 0x0A0B）
  { h0: 0x0a, h1: 0x2a, len: 6 }, // 番茄时长配置
  { h0: 0x0a, h1: 0x2b, len: 11 }, // 番茄运行状态
  { h0: 0x0a, h1: 0x2c, len: 3 }, // 启动/重开番茄 ACK
  { h0: 0x0a, h1: 0x2d, len: 10 }, // 番茄累计完成次数/时长
  { h0: 0x0a, h1: 0x2e, len: 6 }, // 健康提醒页显示时长
]

export interface ScreenSettings {
  brightness: number
  /** 0=30s 1=1min 2=5min 3=10min 4=30min 5=3h */
  lockTime: number
  /** 0=英语 1=简体中文 2=日语 3=德语 */
  language: number
  screensaverOn: boolean
}

/** 屏保轮播状态（0x0A1C，与 adm screensaver-protocol.ts parseCarouselState01c 一致） */
export interface CarouselState {
  enabled: boolean
  count: number
  slot: number
  bitmap: number
  intervalSeconds: number
}

export interface A210cClientHandlers {
  onPortInfo?: (info: PortInfoData) => void
  /** 专注记录（显示器拓展计时）快照回调（0x0A02，随轮询/查询触发） */
  onFocusData?: (info: FocusRecordingData) => void
  onError?: (err: Error) => void
  onDisconnect?: () => void
}

// SerialPort / Reader / Writer 类型 TS DOM lib 里没有，用宽类型避免引入 @types/w3c-web-serial
type AnySerialPort = any
type AnyReader = any
type AnyWriter = any

type FrameWaiter = {
  h0: number
  h1: number
  resolve: (frame: Uint8Array) => void
}

export class A210cClient {
  private port: AnySerialPort
  private reader: AnyReader = null
  private writer: AnyWriter = null

  private rxBuffer: number[] = []
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private firstPollTimeout: ReturnType<typeof setTimeout> | null = null
  private readLoopHandle: Promise<void> | null = null
  private pollSlot = 0
  private stopped = false
  /** 独占传输中（屏保上传等）：暂停轮询，rx 只喂等待器 */
  private transferBusy = false

  /** 是否处于独占传输（屏保上传/下载）中——供串口协调器判断「忙时不让出」 */
  isTransferBusy(): boolean {
    return this.transferBusy
  }
  /** 专注记录指令在飞：跳过本轮 focus 轮询，避免应答错配 */
  private focusBusy = false
  private waiters: FrameWaiter[] = []
  /**
   * 变长帧期望（仅屏保读取 0x0B65 / 0x0A65 用）：这些帧不在 FRAME_TABLE，且 0x0A65
   * 长度随 Info(11B)/Data(7+payload) 变化。设置后由分帧器优先按它切帧；屏保读取期间
   * 轮询已暂停，不会与常规帧交织。
   */
  private expectedVarFrame:
    | { h0: number; h1: number; len: number; resolve: (frame: Uint8Array | null) => void }
    | null = null
  private latestPortInfo: PortInfoData | null = null
  private latestScreenSize: any = null
  private latestScreenSizeAt = 0
  private latestPdAdc: any = null
  private latestPdAdcAt = 0
  private powerSnapshotPending = false
  private powerSnapshotMissCount = 0

  constructor(port: AnySerialPort, private handlers: A210cClientHandlers = {}) {
    this.port = port
  }

  async open(): Promise<void> {
    if (!this.port) throw new Error('A210cClient: no port')
    // 若端口已经打开（例如从其它地方传入），open 会抛 InvalidStateError，忽略
    try {
      await this.port.open({ baudRate: BAUD_RATE })
    } catch (e: any) {
      if (e?.name !== 'InvalidStateError') throw e
    }
    this.writer = this.port.writable.getWriter()
    this.reader = this.port.readable.getReader()
    this.stopped = false
    this.pollSlot = 0
    this.readLoopHandle = this.readLoop()
    this.firstPollTimeout = setTimeout(() => this.pollTick(), FIRST_POLL_DELAY_MS)
    this.pollTimer = setInterval(() => this.pollTick(), POLL_INTERVAL_MS)
  }

  /**
   * 关闭顺序很关键：先停读循环（等它退出并释放读锁），再释放写锁，最后关端口。
   * 若 port.close() 失败（流仍被锁），端口会变成谁也打不开的「僵尸」——必须抛出暴露，
   * 由调用方做端口级兜底，不能静默吞掉。
   */
  async close(): Promise<void> {
    // 关闭前先告知设备「上位机断开」，使其立即回到未连接界面（best-effort）
    try {
      await this.writer?.write(new Uint8Array([0x02, 0x38, HOST_CONN_STATUS_DISCONNECT]))
    } catch {
      /* ignore */
    }
    this.stopped = true
    if (this.firstPollTimeout) {
      clearTimeout(this.firstPollTimeout)
      this.firstPollTimeout = null
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
    try {
      await this.reader?.cancel()
    } catch {
      /* ignore */
    }
    // 等读循环退出（其 finally 已释放读锁），再做后续清理
    await this.readLoopHandle?.catch(() => {})
    this.readLoopHandle = null
    try {
      this.reader?.releaseLock?.()
    } catch {
      /* ignore */
    }
    this.reader = null
    try {
      this.writer?.releaseLock?.()
    } catch {
      /* ignore */
    }
    this.writer = null
    try {
      await this.port?.close?.()
    } catch (e: any) {
      console.error('[a210c-client] port.close 失败（流可能仍被锁定）:', e?.message || e)
      throw e instanceof Error ? e : new Error(String(e))
    }
  }

  /* ---------------- 读循环 / 帧路由 ---------------- */

  private async readLoop(): Promise<void> {
    try {
      while (!this.stopped && this.reader) {
        const { value, done } = await this.reader.read()
        if (done) break
        if (value && value.length > 0) {
          for (let i = 0; i < value.length; i++) this.rxBuffer.push(value[i])
          this.tryConsumeFrames()
        }
      }
    } catch (err: any) {
      if (!this.stopped) {
        this.handlers.onError?.(err instanceof Error ? err : new Error(String(err)))
        this.handlers.onDisconnect?.()
      }
    } finally {
      // 读循环退出即释放读锁，保证 close() 一定能关掉端口（不释放则 port.close 永远失败）
      try {
        this.reader?.releaseLock?.()
      } catch {
        /* close() 侧可能已释放 */
      }
    }
  }

  /** 按 FRAME_TABLE 切帧；头部未对齐时逐字节丢弃 */
  private tryConsumeFrames(): void {
    for (;;) {
      // 屏保读取期间优先按期望变长帧切帧（0x0B65 / 0x0A65）
      const ev = this.expectedVarFrame
      if (ev) {
        while (
          this.rxBuffer.length >= 2 &&
          !(this.rxBuffer[0] === ev.h0 && this.rxBuffer[1] === ev.h1)
        ) {
          this.rxBuffer.shift()
        }
        if (this.rxBuffer.length < ev.len) return
        const frame = new Uint8Array(this.rxBuffer.splice(0, ev.len))
        this.expectedVarFrame = null
        ev.resolve(frame)
        continue
      }
      // 同步到任一已知头
      while (this.rxBuffer.length >= 2) {
        const hit = FRAME_TABLE.some(
          (f) => this.rxBuffer[0] === f.h0 && this.rxBuffer[1] === f.h1,
        )
        if (hit) break
        this.rxBuffer.shift()
      }
      if (this.rxBuffer.length < 2) return
      const entry = FRAME_TABLE.find(
        (f) => this.rxBuffer[0] === f.h0 && this.rxBuffer[1] === f.h1,
      )!
      if (this.rxBuffer.length < entry.len) return
      const frame = new Uint8Array(this.rxBuffer.splice(0, entry.len))
      this.dispatchFrame(frame)
    }
  }

  private dispatchFrame(frame: Uint8Array): void {
    const [h0, h1] = [frame[0], frame[1]]
    // 先喂等待器（请求-应答配对）
    const idx = this.waiters.findIndex((w) => w.h0 === h0 && w.h1 === h1)
    if (idx >= 0) {
      const [w] = this.waiters.splice(idx, 1)
      w.resolve(frame)
      // 功率请求的应答也必须继续广播，不能被等待器消费后跳过页面刷新。
      if (!(h0 === 0x0a && (h1 === 0x36 || h1 === 0x39))) return
    }
    // 无人等待的帧走默认处理（轮询数据）
    try {
      if (h0 === 0x0a && h1 === 0x00) {
        const parsed = Praser.FormatPortInfoData(frame) as PortInfoData | null
        if (parsed) {
          this.latestPortInfo = parsed
          if (!parsed.dpInfo?.connected && this.latestScreenSize) {
            this.latestScreenSize.dpScreenSize = ''
          }
          if (!parsed.hdmiInfo?.connected && this.latestScreenSize) {
            this.latestScreenSize.hdmiScreenSize = ''
          }
          this.emitMergedPortInfo()
          void this.chainPowerSnapshotAfterPortInfo()
        }
      } else if (h0 === 0x0a && h1 === 0x02) {
        const parsed = Praser.FormatFocusRecordData(frame) as FocusRecordingData | null
        if (parsed) this.handlers.onFocusData?.(parsed)
      } else if (h0 === 0x0a && h1 === 0x27) {
        const parsed = Praser.FormatScreenSizeData(frame)
        if (parsed) {
          this.latestScreenSize = parsed
          this.latestScreenSizeAt = Date.now()
          this.emitMergedPortInfo()
        }
      } else if (h0 === 0x0a && (h1 === 0x36 || h1 === 0x39)) {
        const parsed = h1 === 0x39
          ? Praser.FormatPowerSnapshotData(frame)?.pdAdc
          : Praser.FormatPdAdcData(frame)
        if (parsed) {
          if (h1 === 0x39) this.powerSnapshotMissCount = 0
          this.latestPdAdc = parsed
          this.latestPdAdcAt = Date.now()
          this.emitMergedPortInfo()
        }
      }
    } catch (e: any) {
      this.handlers.onError?.(e instanceof Error ? e : new Error(String(e)))
    }
  }

  private emitMergedPortInfo(): void {
    if (!this.latestPortInfo) return
    const now = Date.now()
    const screenSize =
      this.latestScreenSize && now - this.latestScreenSizeAt <= SCREEN_SIZE_CACHE_TTL_MS
        ? this.latestScreenSize
        : null
    const pdAdc =
      this.latestPdAdc && now - this.latestPdAdcAt <= PD_ADC_CACHE_TTL_MS ? this.latestPdAdc : null
    const raw = this.latestPortInfo
    this.handlers.onPortInfo?.({
      ...raw,
      dpInfo: raw.dpInfo?.connected
        ? {
            ...raw.dpInfo,
            screenSize: screenSize?.dpScreenSize || raw.dpInfo.screenSize || '',
          }
        : raw.dpInfo,
      hdmiInfo: raw.hdmiInfo?.connected
        ? {
            ...raw.hdmiInfo,
            screenSize: screenSize?.hdmiScreenSize || raw.hdmiInfo.screenSize || '',
          }
        : raw.hdmiInfo,
      pdAdcInfo: pdAdc || undefined,
    } as PortInfoData)
  }

  /** 等待指定头的下一帧；超时返回 null */
  private waitFrame(h0: number, h1: number, timeoutMs: number): Promise<Uint8Array | null> {
    return new Promise((resolve) => {
      const waiter: FrameWaiter = { h0, h1, resolve: () => {} }
      const timer = setTimeout(() => {
        const i = this.waiters.indexOf(waiter)
        if (i >= 0) this.waiters.splice(i, 1)
        resolve(null)
      }, timeoutMs)
      waiter.resolve = (frame) => {
        clearTimeout(timer)
        resolve(frame)
      }
      this.waiters.push(waiter)
    })
  }

  /** 等待指定头的下一帧并按给定定长切帧（屏保读取的 0x0B65 / 0x0A65 变长帧专用） */
  private waitVarFrame(
    h0: number,
    h1: number,
    len: number,
    timeoutMs: number,
  ): Promise<Uint8Array | null> {
    return new Promise((resolve) => {
      const wrapped = (frame: Uint8Array | null) => {
        clearTimeout(timer)
        resolve(frame)
      }
      const timer = setTimeout(() => {
        if (this.expectedVarFrame?.resolve === wrapped) this.expectedVarFrame = null
        resolve(null)
      }, timeoutMs)
      this.expectedVarFrame = { h0, h1, len, resolve: wrapped }
      // 立即尝试消费已缓冲数据
      this.tryConsumeFrames()
    })
  }

  /* ---------------- 轮询 ---------------- */

  /** 与 ADM 一致：端口帧落地后立即读取功率，不再等待独立轮询时段。 */
  private async chainPowerSnapshotAfterPortInfo(): Promise<void> {
    if (this.stopped || this.transferBusy || this.powerSnapshotPending) return
    this.powerSnapshotPending = true
    try {
      const legacy = this.powerSnapshotMissCount >= POWER_SNAPSHOT_FALLBACK_AFTER
      const op = legacy ? 0x36 : 0x39
      const response = this.waitFrame(0x0a, op, POWER_SNAPSHOT_WAIT_MS)
      const written = await this.write(new Uint8Array([0x02, op]), true)
      const frame = await response
      if (written && !frame && !legacy && !this.stopped && !this.transferBusy) {
        this.powerSnapshotMissCount++
      }
    } finally {
      this.powerSnapshotPending = false
    }
  }

  private pollTick(): void {
    if (this.stopped || this.transferBusy || this.powerSnapshotPending) return
    const kind: PollKind = POLL_PATTERN[this.pollSlot % POLL_PATTERN.length]
    this.pollSlot += 1
    if (kind === 'host-conn') {
      // 上位机连接心跳：告知设备 Web 已连接
      void this.write(new Uint8Array([0x02, 0x38, HOST_CONN_STATUS_CONNECTED]), true)
      return
    }
    if (kind === 'screen-size') {
      void this.write(new Uint8Array([0x02, 0x27]), true)
      return
    }
    void this.write(new Uint8Array([0x02, 0x00]), true)
    // 仅在上层关心专注数据时附带查询（Dashboard 计时面板）
    if (this.handlers.onFocusData && !this.focusBusy) {
      void this.write(new Uint8Array([0x02, 0x0a]), true)
    }
  }

  private async write(data: Uint8Array, reportDisconnect = false): Promise<boolean> {
    if (this.stopped || !this.writer) return false
    try {
      await this.writer.write(data)
      return true
    } catch (e: any) {
      // 写入失败通常意味着设备断连
      if (!this.stopped && reportDisconnect) {
        this.handlers.onError?.(e instanceof Error ? e : new Error(String(e)))
        this.handlers.onDisconnect?.()
      }
      return false
    }
  }

  /* ---------------- 屏幕设置（与 ADM A210C.readScreenSettings 等一致） ---------------- */

  /** 读取屏幕设置 0x0216 → 0x0A16 [_, _, brightness, lockTime, language, screensaverOn, webusb] */
  async readScreenSettings(): Promise<ScreenSettings | null> {
    const rsp = this.waitFrame(0x0a, 0x16, 3000)
    if (!(await this.write(new Uint8Array([0x02, 0x16])))) return null
    const buf = await rsp
    if (!buf) return null
    return {
      brightness: buf[2],
      lockTime: buf[3],
      language: buf[4],
      screensaverOn: buf[5] === 0x01,
    }
  }

  /**
   * 读取固件版本 0x0230 → 0x0A30 + MCU(8B ASCII) + UI/flash(8B ASCII)，\0 填充。
   * UI 版本即 flash 里的 UI 资源版本，主版本 ≥1 = 瓦罗兰特联名版（样机 2.x）。
   * 旧固件不支持 0x230 无应答，超时返回 null。
   */
  async readFwVersion(): Promise<{ mcuVersion: string; uiVersion: string } | null> {
    // 固件在「未连接上位机」状态会忽略业务查询；连接初期主轮询心跳可能还没发出，
    // 查询前先补一帧心跳并稍候，确保设备已切到已连接状态
    await this.write(new Uint8Array([0x02, 0x38, HOST_CONN_STATUS_CONNECTED]))
    await new Promise((resolve) => setTimeout(resolve, 150))
    const rsp = this.waitFrame(0x0a, 0x30, 3000)
    if (!(await this.write(new Uint8Array([0x02, 0x30])))) return null
    const buf = await rsp
    if (!buf) return null
    const dec = new TextDecoder()
    const clean = (b: Uint8Array) => dec.decode(b).replace(/\0/g, '').trim()
    return {
      mcuVersion: clean(buf.subarray(2, 10)),
      uiVersion: clean(buf.subarray(10, 18)),
    }
  }

  /** 亮度 0x0212（UI 范围 20–100，步进 5），无应答；拖动场景直接发，保持丝滑 */
  setBrightness(value: number): void {
    const clamped = Math.max(20, Math.min(100, Math.round(value / 5) * 5))
    void this.write(new Uint8Array([0x02, 0x12, clamped]))
  }

  /** 锁屏时间 0x0211：0=30s 1=1min 2=5min 3=10min 4=30min 5=3h，无应答 */
  async setLockScreenTime(value: number): Promise<boolean> {
    return this.write(new Uint8Array([0x02, 0x11, value & 0xff]))
  }

  /** 设备显示语言 0x0210：0=英语 1=简体中文 2=日语 3=德语，无应答 */
  async setLanguage(value: number): Promise<boolean> {
    return this.write(new Uint8Array([0x02, 0x10, value & 0xff]))
  }

  /** 屏保开关 0x021F，无应答（设备态可用 0x0216 读回确认） */
  async setScreensaverSwitch(enabled: boolean): Promise<boolean> {
    return this.write(new Uint8Array([0x02, 0x1f, enabled ? 0x01 : 0x00]))
  }

  /* ---------------- 专注记录 / 计时动画 ---------------- */

  /** 查询专注记录（0x020A → 0x0A02 42B）；enabled 字段即 Hub Display Timer Animation 开关 */
  async queryFocusRecording(): Promise<FocusRecordingData | null> {
    return this.focusRequest(new Uint8Array([0x02, 0x0a]))
  }

  /** 设置计时动画开关（0x020A + 1 字节），应答带回最新状态 */
  async setTimerAnimation(enabled: boolean): Promise<FocusRecordingData | null> {
    return this.focusRequest(new Uint8Array([0x02, 0x0a, enabled ? 1 : 0]))
  }

  private async focusRequest(req: Uint8Array): Promise<FocusRecordingData | null> {
    this.focusBusy = true
    try {
      const rsp = this.waitFrame(0x0a, 0x02, 5000)
      if (!(await this.write(req))) return null
      const buf = await rsp
      if (!buf) return null
      const parsed = Praser.FormatFocusRecordData(buf) as FocusRecordingData | null
      if (parsed) this.handlers.onFocusData?.(parsed)
      return parsed
    } finally {
      this.focusBusy = false
    }
  }

  /* ---------------- 番茄时钟 / 健康提醒（0x020B、0x020C、0x022A~0x022E） ---------------- */

  /**
   * 串行队列。这族命令没有序号字段，dispatchFrame 按帧头取第一个等待器，
   * 两条同头请求重叠会互相领错应答，故必须「同一时刻仅一条在飞」。
   *
   * 不用 busy 布尔直接丢弃：2.5s 的状态轮询是常驻的，用户拖完滑块或点启动
   * 若正好撞上轮询往返，会拿到 null 被当成失败提示，而请求其实压根没发出去。
   */
  private tomatoQueue: Promise<unknown> = Promise.resolve()

  /** 请求-应答的统一收发；超时、写失败或 CDC 被独占时返回 null */
  private tomatoRequest(
    req: Uint8Array,
    h1: number,
    timeoutMs: number,
  ): Promise<Uint8Array | null> {
    const run = this.tomatoQueue.then(() => this.doTomatoRequest(req, h1, timeoutMs))
    this.tomatoQueue = run.catch(() => undefined)
    return run
  }

  private async doTomatoRequest(
    req: Uint8Array,
    h1: number,
    timeoutMs: number,
  ): Promise<Uint8Array | null> {
    // 屏保上传/回读独占 CDC：期间 expectedVarFrame 会 shift 掉一切非期望字节，
    // 我们的应答会被吃掉，请求字节还会插进对方的整块传输。与 pollTick 同款守卫。
    if (this.stopped || this.transferBusy) return null
    const rsp = this.waitFrame(0x0a, h1, timeoutMs)
    // 写失败也不提前返回：等待器已注册，提前 return 会留下残留等待器，
    // 抢走下一条同头请求的应答。让它走完超时，队列同时形成天然退避。
    await this.write(req)
    return rsp
  }

  /** 番茄时长 / 健康提醒时长共同的取值约束：30～3600 秒且为 30 秒整数倍 */
  private static isValidTomatoSeconds(seconds: number): boolean {
    return (
      Number.isInteger(seconds) && seconds >= 30 && seconds <= 3600 && seconds % 30 === 0
    )
  }

  /** 读番茄时长（0x022A → 0x0A2A 6B），出厂 1500s */
  async getTomatoDuration(): Promise<number | null> {
    const buf = await this.tomatoRequest(new Uint8Array([0x02, 0x2a]), 0x2a, 2000)
    return buf ? Praser.FormatTomatoDurationData(buf) : null
  }

  /**
   * 写番茄时长（0x022A + uint32LE）。
   * 非法值不会生效且设备只回显原配置、不返回错误码，故必须核对回显。
   */
  async setTomatoDuration(seconds: number): Promise<number | null> {
    if (!A210cClient.isValidTomatoSeconds(seconds)) return null
    const req = new Uint8Array([0x02, 0x2a, ...encodeU32LE(seconds)])
    const buf = await this.tomatoRequest(req, 0x2a, 2000)
    return buf ? Praser.FormatTomatoDurationData(buf) : null
  }

  /** 读番茄运行状态（0x022B → 0x0A2B 11B）；旧固件不支持时超时返回 null */
  async getTomatoStatus(): Promise<{
    state: number
    remainingSec: number
    completionSeq: number
  } | null> {
    const buf = await this.tomatoRequest(new Uint8Array([0x02, 0x2b]), 0x2b, 1800)
    return buf ? Praser.FormatTomatoStatusData(buf) : null
  }

  /**
   * 启动 / 重新开始番茄（0x022C → 0x0A2C 3B）。
   * accepted=1 仅表示固件已排队，真正启动需继续轮询 0x022B 直到 state=1。
   */
  async startTomato(): Promise<boolean> {
    const buf = await this.tomatoRequest(new Uint8Array([0x02, 0x2c]), 0x2c, 3000)
    return !!buf && buf.length >= 3 && buf[2] !== 0
  }

  /**
   * 终止当前番茄轮次 —— 固件尚未提供该命令。
   *
   * 设计要求 Cancel = 直接终止（不是重开），但 CDC 协议 v2026-08-05 的
   * 0x022A~0x022E 里没有停止命令：0x022C 语义是「丢弃旧轮次并重新计时」，
   * 设备端目前只能靠单击屏幕退出。
   *
   * 待固件补齐后按下述约定实现即可，UI 无需改动：
   *   请求 2 字节 [0x02, 0x2F]，应答 3 字节 [0x0A, 0x2F, accepted]
   * 同时需在 FRAME_TABLE 增加 { h0: 0x0a, h1: 0x2f, len: 3 }。
   */
  async stopTomato(): Promise<'ok' | 'failed' | 'unsupported'> {
    return 'unsupported'
  }

  /** 读番茄累计完成次数与时长（0x022D → 0x0A2D 10B），只读 */
  async getTomatoStats(): Promise<{
    completedCount: number
    completedSec: number
  } | null> {
    const buf = await this.tomatoRequest(new Uint8Array([0x02, 0x2d]), 0x2d, 2000)
    return buf ? Praser.FormatTomatoStatsData(buf) : null
  }

  /** 读健康提醒页显示时长（0x022E → 0x0A2E 6B），出厂 600s */
  async getHealthReminderDuration(): Promise<number | null> {
    const buf = await this.tomatoRequest(new Uint8Array([0x02, 0x2e]), 0x2e, 2000)
    return buf ? Praser.FormatHealthReminderDurationData(buf) : null
  }

  /** 写健康提醒页显示时长（0x022E + uint32LE），同样需核对 0x0A2E 回显 */
  async setHealthReminderDuration(seconds: number): Promise<number | null> {
    if (!A210cClient.isValidTomatoSeconds(seconds)) return null
    const req = new Uint8Array([0x02, 0x2e, ...encodeU32LE(seconds)])
    const buf = await this.tomatoRequest(req, 0x2e, 2000)
    return buf ? Praser.FormatHealthReminderDurationData(buf) : null
  }

  /** 读专注/健康提醒参数（0x020B → 0x0A03 22B，注意响应头不是 0x0A0B） */
  async getFocusParams(): Promise<{
    breakResetSec: number
    notifyMinFocusSec: number
    timeScale: number
    l1ToL2Sec: number
    l2ToL3Sec: number
  } | null> {
    const buf = await this.tomatoRequest(new Uint8Array([0x02, 0x0b]), 0x03, 1800)
    return buf ? Praser.FormatFocusParamsData(buf) : null
  }

  /**
   * 写专注/健康提醒参数（0x020C，22 字节，无 ACK 且不掉电保存，重启回固件默认）。
   * 写入会重置当前专注会话但保留历史累计；调用方应延时后用 getFocusParams() 回读确认。
   */
  async setFocusParams(params: {
    breakResetSec: number
    notifyMinFocusSec: number
    timeScale: number
    l1ToL2Sec: number
    l2ToL3Sec: number
  }): Promise<boolean> {
    const req = new Uint8Array([
      0x02,
      0x0c,
      ...encodeU32LE(params.breakResetSec),
      ...encodeU32LE(params.notifyMinFocusSec),
      ...encodeU32LE(params.timeScale),
      ...encodeU32LE(params.l1ToL2Sec),
      ...encodeU32LE(params.l2ToL3Sec),
    ])
    return this.write(req)
  }

  /* ---------------- 自定义屏保 ---------------- */

  /** 查询当前屏保 0x0218 → 0x0A18 [slot, crc16(BE)]；crc16=0 表示设备无屏保 */
  async queryCurrentScreensaver(): Promise<{ slot: number; crc16: number } | null> {
    const rsp = this.waitFrame(0x0a, 0x18, 3000)
    if (!(await this.write(new Uint8Array([0x02, 0x18])))) return null
    const buf = await rsp
    if (!buf) return null
    return { slot: buf[2], crc16: (buf[3] << 8) | buf[4] }
  }

  /**
   * 上传屏保素材（0x0220 Info → 0x0A20，循环 0x0221 Data → 0x0A21）。
   * 期间暂停轮询独占 CDC；失败抛错。
   */
  async uploadScreensaver(
    jpeg: Uint8Array,
    crc16: number,
    onProgress?: (percent: number) => void,
  ): Promise<void> {
    if (this.transferBusy) throw new Error('上一次传输尚未结束')
    this.transferBusy = true
    try {
      // 等在途轮询响应落地并清缓冲，避免旧帧干扰
      await new Promise((r) => setTimeout(r, TRANSFER_SETTLE_MS))
      this.rxBuffer = []

      const infoRsp = this.waitFrame(0x0a, 0x20, 60000)
      if (!(await this.write(buildScreensaverInfoPacket(jpeg.length, crc16)))) {
        throw new Error('写入失败（设备可能已断开）')
      }
      const info = await infoRsp
      if (!info) throw new Error('0x0A20 应答超时')
      if (info[2] !== 0x00) {
        const errNames: Record<number, string> = { 1: 'busy', 2: 'backup_fail', 3: 'crc_err' }
        throw new Error(`设备拒绝上传: ${errNames[info[2]] || '0x' + info[2].toString(16)}`)
      }

      // 完成监听必须在发包循环之前挂上（末包后 MCU 立即回包，后挂会丢）
      const doneRsp = this.waitFrame(0x0a, 0x21, 30000)
      const totalPkts = Math.ceil(jpeg.length / SS_PKT_DATA_LEN)
      for (let seq = 0; seq < totalPkts; seq++) {
        const start = seq * SS_PKT_DATA_LEN
        const chunk = jpeg.subarray(start, Math.min(start + SS_PKT_DATA_LEN, jpeg.length))
        if (!(await this.write(buildScreensaverDataPacket(seq, chunk)))) {
          throw new Error('写入失败（设备可能已断开）')
        }
        onProgress?.(Math.round(((seq + 1) / totalPkts) * 100))
      }

      const done = await doneRsp
      if (!done) throw new Error('0x0A21 完成应答超时')
      if (done[2] !== 0x10) {
        throw new Error(done[2] === 0x11 ? 'MCU 写入失败 (0x11)' : `未知应答 0x${done[2].toString(16)}`)
      }
    } finally {
      this.transferBusy = false
    }
  }

  /** 设为当前屏保（0x0219 按 CRC），无应答包 */
  async setScreensaverByCrc(crc16: number): Promise<boolean> {
    return this.write(buildSetScreensaverByCrc(crc16))
  }

  /**
   * 从设备读取当前屏保 JPEG（0x0365 握手 → 0x0265 Info → 逐包 0x0265 Data）。
   * 与 ADM readScreensaverJpegFromMcu 对齐：暂停轮询独占 CDC + 静默期，整段失败重试。
   * 返回裸 Baseline JPEG 字节；槽位为空返回长度 0 的数组；失败返回 null。
   */
  async readScreensaverJpeg(): Promise<Uint8Array | null> {
    if (this.transferBusy) throw new Error('上一次传输尚未结束')
    this.transferBusy = true
    try {
      // 等在途轮询响应落地并清缓冲，避免端口/PD-ADC 帧与屏保 Flash 读取交织
      await new Promise((r) => setTimeout(r, TRANSFER_SETTLE_MS))
      const MAX_RETRY = 3
      for (let attempt = 0; attempt <= MAX_RETRY; attempt++) {
        if (attempt > 0) await new Promise((r) => setTimeout(r, 600))
        const r = await this.fetchScreensaverJpegOnce()
        if (r.ok) return r.jpeg ?? null
        if (r.fatal) break
      }
      return null
    } finally {
      this.expectedVarFrame = null
      this.transferBusy = false
    }
  }

  private async fetchScreensaverJpegOnce(): Promise<{
    ok: boolean
    jpeg?: Uint8Array
    fatal?: boolean
  }> {
    this.rxBuffer = []
    this.expectedVarFrame = null

    // 握手 0x0365 → 0x0B65（3B）
    let p = this.waitVarFrame(0x0b, 0x65, 3, 5000)
    if (!(await this.write(buildReadHandshake365()))) return { ok: false }
    const hs = await p
    if (!hs || hs.length < 3) return { ok: false }
    if (hs[2] === 0x03) return { ok: false } // BUSY：设备正在加载屏保，可重试
    if (hs[2] !== 0x00) return { ok: false }

    // Info 0x0265 → 0x0A65（11B）
    p = this.waitVarFrame(0x0a, 0x65, 11, 8000)
    if (!(await this.write(buildReadInfo265()))) return { ok: false }
    const infoBuf = await p
    if (!infoBuf) return { ok: false }
    const info = parseReadInfo065(infoBuf)
    if (!info) return { ok: false }
    if (info.status === 0x01) return { ok: true, jpeg: new Uint8Array(0), fatal: true } // 槽位为空
    if (info.status === 0x03) return { ok: false } // 设备忙，可重试
    if (info.status !== 0x00) return { ok: false }
    const { imgSize, totalPkts } = info
    if (imgSize === 0 || totalPkts === 0) {
      return { ok: true, jpeg: new Uint8Array(0), fatal: true }
    }

    // 逐包拉取 0x0265 0x01 seq → 0x0A65（7+payload）
    const chunks: Uint8Array[] = []
    for (let seq = 0; seq < totalPkts; seq++) {
      const payLen = Math.min(
        SS_READ_PAYLOAD_MAX,
        Math.max(0, imgSize - seq * SS_READ_PAYLOAD_MAX),
      )
      const wp = this.waitVarFrame(0x0a, 0x65, 7 + payLen, 8000)
      if (!(await this.write(buildReadData265(seq)))) return { ok: false }
      const db = await wp
      if (!db) return { ok: false }
      const parsed = parseReadData065(db)
      if (!parsed || parsed.status !== 0x00 || parsed.seq !== seq) return { ok: false }
      chunks.push(parsed.payload)
    }
    const total = chunks.reduce((n, c) => n + c.length, 0)
    if (total !== imgSize) return { ok: false }
    const out = new Uint8Array(total)
    let off = 0
    for (const c of chunks) {
      out.set(c, off)
      off += c.length
    }
    return { ok: true, jpeg: out }
  }

  /** 删除屏保素材（0x021A 按 CRC → 0x0A1A：0=成功 1=未找到，未找到也视为已删） */
  async deleteScreensaverByCrc(crc16: number): Promise<boolean> {
    const rsp = this.waitFrame(0x0a, 0x1a, 3000)
    if (!(await this.write(new Uint8Array([0x02, 0x1a, (crc16 >> 8) & 0xff, crc16 & 0xff])))) {
      return false
    }
    const buf = await rsp
    if (!buf) return false
    return buf[2] === 0x00 || buf[2] === 0x01
  }

  /**
   * 查询屏保轮播状态（0x021C → 0x0A1C [on][count][slot][bitmap][interval BE]）。
   * ⚠️ 固件约束：轮播开启中禁止 0x0365/0x0265 回读屏保，任何回读前须先查本状态。
   */
  async getCarouselState(): Promise<CarouselState | null> {
    const rsp = this.waitFrame(0x0a, 0x1c, 3000)
    if (!(await this.write(new Uint8Array([0x02, 0x1c])))) return null
    const buf = await rsp
    if (!buf) return null
    return {
      enabled: buf[2] === 0x01,
      count: buf[3],
      slot: buf[4],
      bitmap: buf[5],
      intervalSeconds: (buf[6] << 8) | buf[7],
    }
  }

  /* ---------------- 恢复出厂 ---------------- */

  /**
   * 恢复出厂 0x030A → 0x0B0A + 状态（0x00=已接受；旧固件 0xCE）。
   * MCU 接受后会立即复位 → 串口断开属预期（store 的 disconnect 流程接管导航）。
   * 写入后短时间内断开也视为已接受（与 ADM 桌面端口径一致）。
   */
  async factoryReset(): Promise<boolean> {
    const rsp = this.waitFrame(0x0b, 0x0a, 5000)
    const wrote = await this.write(new Uint8Array([0x03, 0x0a]))
    if (!wrote) return false
    const buf = await rsp
    if (buf) return buf[2] === 0x00 || buf[2] === 0xce
    // 应答超时：若期间设备已复位断连，读循环会触发 onDisconnect；按已接受处理
    return !this.port?.readable
  }
}
